"use client";

import type { ReactNode } from "react";
import { useTodayYmd } from "@/hooks/use-today-ymd";
import { ANY_TIME_ID, LIVE_TIME_ID, chosenGameDays, gameTimeLabel, listedDays, pickGameTime, soonId, type GameTime, type TimeChoices } from "@/lib/game-date";
import { chipClass, LIVE_DOT, SectionLabel } from "./chrome";
import { CrumbMenu, type CrumbItem } from "./crumb-menu";

type StartTimeProps = { time: GameTime; times: TimeChoices; liveChoice?: boolean; onTime: (time: GameTime) => void };
export type BreaksChoice = { readonly active: boolean; readonly onPick: () => void };

const BREAKS_ID = "breaks";
const BREAK_DOT = <span className="h-1.5 w-1.5 rounded-full bg-zinc-500" aria-hidden />;

export function StartTimeMenu({ time, times, isMobile, liveChoice = false, breaks, onTime }: StartTimeProps & { isMobile: boolean; breaks?: BreaksChoice }) {
  const todayYmd = useTodayYmd();
  const chosenDays = new Set(chosenGameDays(time, todayYmd));
  const inBreak = breaks?.active === true;
  const timeItems: CrumbItem[] = [
    { id: ANY_TIME_ID, label: "Any time", active: !time.live && time.within === 0 && chosenDays.size === 0 },
    ...(liveChoice ? [{ id: LIVE_TIME_ID, label: "Live", meta: "Beta", icon: LIVE_DOT, active: time.live && !inBreak }] : []),
    ...(breaks ? [{ id: BREAKS_ID, label: "In a break", icon: BREAK_DOT, active: inBreak }] : []),
    ...times.soon.map((soon) => ({ id: soonId(soon.hours), label: soon.label, meta: soon.count === null ? undefined : String(soon.count), active: time.within === soon.hours, group: "Starting soon" })),
    ...listedDays(times.days, time, todayYmd).map((day) => ({ id: day.value, label: day.label, meta: day.count === null ? undefined : String(day.count), active: chosenDays.has(day.value), group: "Days", multiple: true })),
  ];
  return (
    <CrumbMenu
      title="Start time"
      label={inBreak ? "In a break" : gameTimeLabel(time, todayYmd)}
      icon={inBreak ? BREAK_DOT : time.live ? LIVE_DOT : undefined}
      items={timeItems}
      isMobile={isMobile}
      tone="quiet"
      className="shrink-0"
      onPick={(id) => (id === BREAKS_ID && breaks ? breaks.onPick() : onTime(pickGameTime(time, id, todayYmd)))}
    />
  );
}

export function StartTimeSection({ time, times, tall, liveChoice = false, onTime }: StartTimeProps & { tall: boolean }) {
  const todayYmd = useTodayYmd();
  const chosenDays = new Set(chosenGameDays(time, todayYmd));
  const timeChip = (id: string, label: ReactNode, active: boolean, icon?: ReactNode) => (
    <button key={id} type="button" aria-pressed={active} onClick={() => onTime(pickGameTime(time, id, todayYmd))} className={chipClass(active, tall)}>{icon}{label}</button>
  );
  return (
    <section aria-label="Start time">
      <SectionLabel>Start time</SectionLabel>
      <div className="flex flex-col gap-1.5 px-1.5">
        <div className="flex flex-wrap gap-1.5">
          {timeChip(ANY_TIME_ID, "Any time", !time.live && time.within === 0 && chosenDays.size === 0)}
          {liveChoice && timeChip(LIVE_TIME_ID, <>Live <span className="text-[11px] text-zinc-500">Beta</span></>, time.live, LIVE_DOT)}
          <div role="group" aria-label="Starting soon" className="contents">
            {times.soon.map((soon) => timeChip(soonId(soon.hours), soon.label, time.within === soon.hours))}
          </div>
        </div>
        <div role="group" aria-label="Days" className="flex flex-wrap gap-1.5">
          {listedDays(times.days, time, todayYmd).map((day) => timeChip(day.value, day.label, chosenDays.has(day.value)))}
        </div>
      </div>
    </section>
  );
}
